// PayQR.js - Modal para mostrar código QR de pago
import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Alert, Spinner, Form } from 'react-bootstrap';
import { 
    FaQrcode, 
    FaMobileAlt, 
    FaCheckCircle, 
    FaExclamationTriangle,
    FaUpload,
    FaArrowRight 
} from 'react-icons/fa';
import { API_URL, API_ENDPOINTS } from '../config/api';

const PayQR = ({ show, onHide, payment, onPaymentSuccess }) => {
    // ===== ESTADOS DEL COMPONENTE =====
    const [loading, setLoading] = useState(false);
    const [paymentCompleted, setPaymentCompleted] = useState(false);
    const [error, setError] = useState(null);
    const [qrImageLoaded, setQrImageLoaded] = useState(false);
    
    // NUEVOS ESTADOS PARA CONFIGURACIÓN DINÁMICA
    const [paymentConfigs, setPaymentConfigs] = useState([]);
    const [loadingConfigs, setLoadingConfigs] = useState(true);
    const [activeConfigIndex, setActiveConfigIndex] = useState(0);

    // ===== FUNCIONES DE UTILIDAD =====
    
    // Formatear moneda a formato ecuatoriano
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || isNaN(Number(amount))) {
            return '$0.00';
        }
        const numAmount = Number(amount);
        return `$${numAmount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
    };

    // Formatear fecha para mostrar
    const formatDateDisplay = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Fecha inválida';
            return date.toLocaleDateString('es-EC', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
            });
        } catch (e) {
            console.error("Error formateando fecha:", dateString, e);
            return "Fecha inválida";
        }
    };

 const loadPaymentConfigs = async () => {
    if (!payment || !payment.id) {
        console.warn('⚠️ No hay payment o payment.id');
        return;
    }
    
    setLoadingConfigs(true);
    setError(null); // Limpiar errores previos
    
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No autenticado');
        }
        
        console.log(`🔍 Buscando configs para payment ID: ${payment.id}`);
        
        const response = await fetch(API_ENDPOINTS.paymentConfigByPayment(payment.id), {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        
        console.log('📡 Response status:', response.status);
        
        if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('📦 Datos recibidos:', data);
        
        if (data.configs && data.configs.length > 0) {
            console.log(`✅ ${data.configs.length} configuración(es) encontrada(s)`);
            setPaymentConfigs(data.configs);
        } else {
            console.warn('⚠️ No se encontraron configuraciones');
            setError('No hay métodos de pago configurados para este condominio. Contacta al administrador.');
            setPaymentConfigs([]);
        }
    } catch (err) {
        console.error('❌ Error al cargar configuraciones:', err);
        setError(err.message || 'Error al cargar métodos de pago');
        setPaymentConfigs([]);
    } finally {
        setLoadingConfigs(false);
    }
};
    // ===== EFECTOS =====
    
    // Resetear estados cuando se abre/cierra el modal
    useEffect(() => {
        if (show) {
            setLoading(false);
            setPaymentCompleted(false);
            setError(null);
            setQrImageLoaded(false);
            setPaymentConfigs([]);
            setActiveConfigIndex(0);
            loadPaymentConfigs();
            
            console.log('Modal QR abierto para pago:', payment);
        } else {
            setPaymentCompleted(false);
            setError(null);
        }
    }, [show, payment]);

    // ===== FUNCIONES DE MANEJO DE EVENTOS =====
    
    // Función cuando el usuario confirma que realizó el pago
    const handlePaymentConfirmation = async () => {
        setLoading(true);
        setError(null);

        try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            setPaymentCompleted(true);
            setLoading(false);
            
            

            setTimeout(() => {
                handleClose();
            }, 3000);
            
        } catch (err) {
            setError('Error al procesar la confirmación. Inténtelo de nuevo.');
            setLoading(false);
        }
    };

    // Función para cerrar el modal
    const handleClose = () => {
        if (!loading) {
            setPaymentCompleted(false);
            setError(null);
            setQrImageLoaded(false);
            onHide();
        }
    };

    // Función cuando la imagen del QR se carga exitosamente
    const handleImageLoad = () => {
        setQrImageLoaded(true);
    };

    // Función cuando la imagen del QR falla al cargar
    const handleImageError = () => {
        setError('No se pudo cargar el código QR.');
    };

    // ===== VALIDACIÓN =====
    if (!payment) {
        return null;
    }

    // ===== RENDER DEL COMPONENTE =====
    return (
        <Modal 
            show={show} 
            onHide={handleClose} 
            backdrop="static"
            keyboard={!loading}
            centered
            size="md"
            className="payqr-modal"
        >
            {/* ===== HEADER DEL MODAL ===== */}
            <Modal.Header 
                closeButton={!loading} 
                className="bg-info text-white"
            >
                <Modal.Title className="d-flex align-items-center">
                    <FaQrcode className="me-2" />
                    Pagar con Código QR
                </Modal.Title>
            </Modal.Header>
            
            {/* ===== BODY DEL MODAL ===== */}
            <Modal.Body>
                {/* MENSAJE DE CARGA */}
                {loading && !paymentCompleted && (
                    <Alert variant="info" className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Procesando confirmación de pago...
                    </Alert>
                )}
                
                {/* MENSAJE DE ÉXITO */}
                {paymentCompleted && (
                    <Alert variant="success">
                        <Alert.Heading className="d-flex align-items-center">
                            <FaCheckCircle className="me-2" />
                            ¡Proceso Completado!
                        </Alert.Heading>
                        <p className="mb-2">
                            Has confirmado el pago con código QR. 
                        </p>
                        <hr />
                        <p className="mb-0">
                            <strong>📋 Siguiente paso:</strong> Sube tu comprobante de pago 
                            usando el botón "Subir Comp." en la columna "Acciones" para 
                            que tu pago sea validado por la administración.
                        </p>
                    </Alert>
                )}
                
                {/* MENSAJE DE ERROR */}
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        <strong>Error:</strong> {error}
                    </Alert>
                )}

                {/* CONTENIDO PRINCIPAL - Solo mostrar si no hay éxito */}
                {!paymentCompleted && (
                    <>
                        {/* ===== DETALLES DEL PAGO ===== */}
                        <Card className="mb-3 bg-light border-primary">
                            <Card.Body>
                                <Card.Title as="h6" className="text-primary mb-3">
                                    📄 Detalles del Pago
                                </Card.Title>
                                
                                <div className="row">
                                    <div className="col-md-6">
                                        <p className="mb-2">
                                            <strong>Concepto:</strong><br />
                                            <span className="text-muted">
                                                {payment?.concept || 'N/A'}
                                            </span>
                                        </p>
                                        <p className="mb-2">
                                            <strong>Fecha Límite:</strong><br />
                                            <span className="text-muted">
                                                {formatDateDisplay(payment?.due_date || payment?.created_at)}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="mb-2">
                                            <strong>Monto a Pagar:</strong><br />
                                            <span className="text-danger fw-bold fs-4">
                                                {formatCurrency(payment?.amount)}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                
                                {payment?.description && (
                                    <div className="mt-2 p-2 bg-white rounded border">
                                        <small>
                                            <strong>Descripción:</strong><br />
                                            {payment.description}
                                        </small>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* ===== SECCIÓN DEL CÓDIGO QR ===== */}
                        <Card className="mb-3 border-success">
                            <Card.Body className="text-center">
                                <Card.Title as="h6" className="text-success mb-3">
                                    💳 Métodos de Pago
                                </Card.Title>
                                
                                {/* CONTENEDOR DEL QR */}
                                <div className="qr-container p-3 bg-white border rounded mb-3">
                                    {loadingConfigs ? (
                                        <div className="py-4">
                                            <Spinner animation="border" className="text-primary" />
                                            <p className="mt-2 text-muted">Cargando métodos de pago...</p>
                                        </div>
                                    ) : paymentConfigs.length === 0 ? (
                                        <Alert variant="warning">
                                            <FaExclamationTriangle className="me-2" />
                                            No hay métodos de pago disponibles. Contacta al administrador.
                                        </Alert>
                                    ) : (
                                        <>
                                            {/* Selector de métodos si hay múltiples */}
                                            {paymentConfigs.length > 1 && (
                                                <div className="mb-3">
                                                    <Form.Select
                                                        value={activeConfigIndex}
                                                        onChange={(e) => setActiveConfigIndex(parseInt(e.target.value))}
                                                        size="sm"
                                                    >
                                                        {paymentConfigs.map((config, index) => (
                                                            <option key={config.id} value={index}>
                                                                {config.bank_name} - {config.qr_image_path ? 'Código QR' : `Cuenta ${config.account_number}`}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </div>
                                            )}
                                            
                                            {/* Mostrar configuración activa */}
                                            {paymentConfigs[activeConfigIndex]?.qr_image_path ? (
                                                <div className="qr-image-container">
                                                    {!qrImageLoaded && !error && (
                                                        <div className="py-4">
                                                            <Spinner animation="border" className="text-primary" />
                                                            <p className="mt-2 text-muted">Cargando código QR...</p>
                                                        </div>
                                                    )}
                                                    
                                         <img 
    src={`${API_URL.replace('/api', '')}/${paymentConfigs[activeConfigIndex].qr_image_path.replace(/\\/g, '/')}`}
    alt={`Código QR - ${paymentConfigs[activeConfigIndex].bank_name}`}
    onLoad={() => {
        handleImageLoad();
        console.log('✅ QR cargado exitosamente');
    }}
    onError={(e) => {
        console.error('❌ Error cargando QR:', e.target.src);
        handleImageError();
    }}
    className="img-fluid mb-2"
    style={{ maxWidth: '300px', height: 'auto', display: 'block', margin: '0 auto' }}
/>
{/* Fallback si la imagen no existe */}
{error && paymentConfigs[activeConfigIndex]?.qr_image_path && (
    <Alert variant="warning" className="mt-3">
        <FaExclamationTriangle className="me-2" />
        <strong>Imagen QR no disponible</strong>
        <p className="mb-0 mt-2 small">
            El código QR no pudo cargarse. Por favor:
            <br />• Solicita al administrador que vuelva a cargar el QR
            <br />• O realiza tu pago por transferencia bancaria a la cuenta mostrada
        </p>
    </Alert>
)}

                            
                                                    
                                                    {qrImageLoaded && (
                                                        <div className="mt-3">
                                                            <p className="mb-1 small text-success fw-bold">
                                                                ✅ Código QR listo para escanear
                                                            </p>
                                                            <p className="mb-0 small text-muted">
                                                                {paymentConfigs[activeConfigIndex].bank_name}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <Alert variant="info">
                                                    <p className="mb-2"><strong>Banco:</strong> {paymentConfigs[activeConfigIndex].bank_name}</p>
                                                    <p className="mb-0"><strong>Número de Cuenta:</strong> {paymentConfigs[activeConfigIndex].account_number}</p>
                                                    <hr />
                                                    <small className="text-muted">
                                                        Realiza tu transferencia a esta cuenta y luego sube tu comprobante.
                                                    </small>
                                                </Alert>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* ===== INSTRUCCIONES PASO A PASO ===== */}
                                <div className="instructions-container">
                                    <h6 className="text-info mb-3">
                                        📋 Instrucciones de Pago:
                                    </h6>
                                    
                                    <div className="row text-center mb-3">
                                        <div className="col-4">
                                            <div className="p-2">
                                                <FaMobileAlt size={28} className="text-primary mb-2" />
                                                <p className="small mb-0">
                                                    <strong>1. Abrir App</strong><br />
                                                    <span className="text-muted">App Bancaria</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="p-2">
                                                <FaQrcode size={28} className="text-success mb-2" />
                                                <p className="small mb-0">
                                                    <strong>2. Escanear</strong><br />
                                                    <span className="text-muted">Código QR</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="p-2">
                                                <FaUpload size={28} className="text-warning mb-2" />
                                                <p className="small mb-0">
                                                    <strong>3. Subir</strong><br />
                                                    <span className="text-muted">Comprobante</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-start">
                                        <ol className="small">
                                            <li>Abre tu app bancaria</li>
                                            <li>Busca la opción <strong>"Pagar con QR"</strong></li>
                                            <li>Escanea el código QR mostrado arriba</li>
                                            <li>Confirma el monto: <strong>{formatCurrency(payment?.amount)}</strong></li>
                                            <li>Completa el pago</li>
                                            <li>📄 Regresa aquí y confirma que pagaste</li>
                                            <li>📄 Luego sube tu comprobante en "Acciones"</li>
                                        </ol>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* ===== NOTA IMPORTANTE ===== */}
                        <Alert variant="warning" className="mb-0">
                            <FaExclamationTriangle className="me-2" />
                            <strong>Importante:</strong> Después de realizar el pago, 
                            debes subir tu comprobante usando el botón 
                            <strong className="text-primary"> "Subir Comp."</strong> en la 
                            columna "Acciones" para que tu pago sea validado oficialmente.
                        </Alert>
                    </>
                )}
            </Modal.Body>

            {/* ===== FOOTER DEL MODAL ===== */}
            <Modal.Footer>
                {!paymentCompleted && (
                    <div className="d-flex justify-content-between w-100">
                        <Button 
                            variant="secondary" 
                            onClick={handleClose} 
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        
                  <Button
    variant="success"
    onClick={handlePaymentConfirmation}
    disabled={
        loading || 
        loadingConfigs || 
        paymentConfigs.length === 0 ||
        (paymentConfigs[activeConfigIndex]?.qr_image_path && !qrImageLoaded)
    }
    className="d-flex align-items-center"
>
                            {loading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Confirmando...
                                </>
                            ) : (
                                <>
                                    <FaCheckCircle className="me-2" />
                                    Ya realicé el pago
                                    <FaArrowRight className="ms-2" />
                                </>
                            )}
                        </Button>
                    </div>
                )}
                
                {paymentCompleted && (
                    <Button variant="primary" onClick={handleClose}>
                        Entendido
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default PayQR;